import {
  History as HistoryIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useFilteredNavigation } from "../../hooks/useFilteredNavigation";
import { useGetVisitedQuery } from "../../store/services/customerService";
import { useGetSalesEmployeesQuery } from "../../store/services/customerService";
import {
  removeAuth,
  selectCompany,
  selectUserNo,
} from "../../store/slices/authSlice";
import RecentProfilesModal from "../last-visited/RecentProfilesModal";
import WorkItemCalendarSync from "../workitem-calendar-sync/WorkItemCalendarSync";

const TopNavigation: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const company = useSelector(selectCompany);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const userNo = useSelector(selectUserNo);
  const { data: employees = [] } = useGetSalesEmployeesQuery();
  const { data: recentVisits = [] } = useGetVisitedQuery();
  const { navigationItems } = useFilteredNavigation();
  const user = employees.find(
    (employee) => userNo && employee.ExternalUserNo === +userNo
  );

  // State for the mobile menu
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] =
    useState<null | HTMLElement>(null);

  // State for active section (to show the appropriate submenu)
  const [activeSection, setActiveSection] = useState<string>(() => {
    // Initialize based on current path
    const path = location.pathname;
    if (path.includes("dashboard") || path.includes("calendar"))
      return "Dashboard";
    if (path.includes("customer") || path.includes("email")) return "Kunder";
    if (path.includes("report") || path.includes("statistics"))
      return "Rapporter";
    return "Dashboard"; // Default
  });

  // State for user profile menu
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(
    null
  );

  // State for recent profiles modal
  const [recentModalOpen, setRecentModalOpen] = useState(false);

  // Use filtered navigation items instead of hardcoded menu items
  const menuItems = navigationItems;

  // Find the active menu item based on the activeSection
  const activeMenuItem = menuItems.find((item) => item.text === activeSection);

  // Handle mobile menu opening
  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchorEl(event.currentTarget);
  };

  // Handle section change when clicking on a top-level menu item
  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    // Find the default path for this section (first subitem's path)
    const menuItem = menuItems.find((item) => item.text === section);
    if (menuItem && menuItem.subItems && menuItem.subItems.length > 0) {
      navigate(menuItem.subItems[0].path);
    }
  };

  // Handle menu closings for user menu
  const handleMenuClose = (
    setter: React.Dispatch<React.SetStateAction<HTMLElement | null>>
  ) => {
    setter(null);
  };

  // Handle navigation for submenu items
  const handleNavigation = (path: string) => {
    navigate(path);
    // Close mobile menu if open
    setMobileMenuAnchorEl(null);
  };

  // Handle logout
  const handleLogout = () => {
    dispatch(removeAuth());
    navigate("/login");
  };

  // Determine logo and primary color based on company
  const getCompanyStyle = () => {
    switch (company) {
      case "Enkla":
        return {
          logo: "Enkla Elbolaget",
          color: "#FF5F00", // Orange
        };
      case "Dalakraft":
      default:
        return {
          logo: "Dalakraft",
          color: "#005AA9", // Blue
        };
    }
  };

  const companyStyle = getCompanyStyle();

  // Check if we're in test environment
  const isTestEnvironment =
    import.meta.env.VITE_NAV_API_URL?.includes("api-t.kraftsamling.se") ||
    import.meta.env.VITE_NAV_API_URL?.includes("localhost");

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="static"
        sx={{
          bgcolor: "white",
          color: "text.primary",
          boxShadow: 1,
        }}
      >
        <Toolbar>
          {/* Company Logo/Name */}
          <Box
            sx={{
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              gap: 2,
              mr: 4,
            }}
          >
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                fontWeight: "bold",
                color: companyStyle.color,
              }}
            >
              {companyStyle.logo}
            </Typography>
            {isTestEnvironment && (
              <Chip
                label="TEST"
                size="small"
                sx={{
                  bgcolor: "warning.main",
                  color: "warning.contrastText",
                  fontWeight: "bold",
                  fontSize: "0.7rem",
                  height: 24,
                }}
              />
            )}
          </Box>
          {/* Mobile menu icon */}
          {isMobile ? (
            <>
              <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={handleMobileMenuOpen}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  variant="h6"
                  component="div"
                  sx={{ color: companyStyle.color }}
                >
                  Kraftvyn B2B
                </Typography>
                {isTestEnvironment && (
                  <Chip
                    label="TEST"
                    size="small"
                    sx={{
                      bgcolor: "warning.main",
                      color: "warning.contrastText",
                      fontWeight: "bold",
                      fontSize: "0.6rem",
                      height: 20,
                    }}
                  />
                )}
              </Box>
            </>
          ) : (
            // Desktop Navigation Items
            <Box sx={{ flexGrow: 1, display: "flex" }}>
              {/* Map through top level menu items */}
              {menuItems.map((item) => (
                <Button
                  key={item.text}
                  color="inherit"
                  onClick={() => handleSectionChange(item.text)}
                  sx={{
                    mr: 2,
                    borderBottom:
                      activeSection === item.text
                        ? `2px solid ${companyStyle.color}`
                        : "none",
                    borderRadius: 0,
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                      borderBottom: `2px solid ${companyStyle.color}`,
                    },
                  }}
                >
                  {<item.icon />}
                  <Typography sx={{ ml: 1 }}>{item.text}</Typography>
                </Button>
              ))}
            </Box>
          )}
          <IconButton
            color="primary"
            sx={{ ml: 1 }}
            onClick={() => setRecentModalOpen(true)}
            title="Senaste kunder"
          >
            <HistoryIcon />
          </IconButton>
          <WorkItemCalendarSync />
          {/* User profile button - visible on both mobile and desktop */}
          <Tooltip title="Konto inställningar">
            <IconButton
              onClick={(e) => {
                setUserMenuAnchorEl(e.currentTarget);
              }}
              sx={{ p: 0 }}
              aria-controls="menu-appbar"
              aria-haspopup="true"
            >
              <Avatar
                sx={{
                  border: `2px solid ${companyStyle.color}`,
                }}
              >
                {user?.Name[0]}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            id="menu-appbar"
            anchorEl={userMenuAnchorEl}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(userMenuAnchorEl)}
            onClose={() => handleMenuClose(setUserMenuAnchorEl)}
          >
            <MenuItem
              onClick={() => handleNavigation(`/users/${userNo}/activity`)}
            >
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Min profil</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleNavigation("/settings")}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Inställningar</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logga ut</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Persistent Submenu - Always visible on mobile */}
      {/* Persistent Submenu - Always visible on desktop */}
      {!isMobile && activeMenuItem && activeMenuItem.subItems && (
        <Paper
          elevation={0}
          square
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.grey[50],
            display: "flex",
            justifyContent: "flex-start",
            px: 2,
          }}
        >
          {activeMenuItem.subItems.map((subItem) => (
            <Button
              key={subItem.path}
              color="inherit"
              size="small"
              startIcon={<subItem.icon fontSize="small" />}
              onClick={() => handleNavigation(subItem.path)}
              sx={{
                mx: 1,
                py: 1,
                textTransform: "none",
                color:
                  location.pathname === subItem.path
                    ? companyStyle.color
                    : "text.primary",
                fontWeight:
                  location.pathname === subItem.path ? "bold" : "normal",
              }}
            >
              {subItem.text}
            </Button>
          ))}
        </Paper>
      )}

      {/* Mobile menu */}
      <Menu
        id="mobile-menu"
        anchorEl={mobileMenuAnchorEl}
        keepMounted
        open={Boolean(mobileMenuAnchorEl)}
        onClose={() => handleMenuClose(setMobileMenuAnchorEl)}
        PaperProps={{
          style: {
            width: "100%",
            maxWidth: "300px",
          },
        }}
      >
        {menuItems.map((category) => (
          <Box key={category.text}>
            <MenuItem
              sx={{
                bgcolor: theme.palette.grey[100],
                fontWeight: "bold",
              }}
              onClick={() => handleSectionChange(category.text)}
            >
              <ListItemIcon>{<category.icon />}</ListItemIcon>
              <ListItemText>{category.text}</ListItemText>
            </MenuItem>
            {category.subItems?.map((item) => (
              <MenuItem
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                sx={{ pl: 4 }}
              >
                <ListItemIcon>{<item.icon fontSize="small" />}</ListItemIcon>
                <ListItemText>{item.text}</ListItemText>
              </MenuItem>
            ))}
            <Divider />
          </Box>
        ))}
        <MenuItem onClick={() => handleNavigation("/settings")}>
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText>Inställningar</ListItemText>
        </MenuItem>
      </Menu>

      {/* Recent Profiles Modal */}
      <RecentProfilesModal
        visits={recentVisits}
        open={recentModalOpen}
        onClose={() => setRecentModalOpen(false)}
        onSelect={(customerId) => {
          setRecentModalOpen(false);
          navigate(`/customer/${customerId}`);
        }}
      />
    </Box>
  );
};

export default TopNavigation;
